import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  WorkdayEndpointConfig,
  WorkdayIntegrationConfig,
  WorkdayWriteField,
  WorkdayWriteFieldType
} from '@dadohr/shared/domainTypes/integrations/workday';
import styled from '@emotion/styled';
import { Button, Dialog, MenuItem, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { every, isEqual } from 'lodash';
import { PlusCircle, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import shortid from 'shortid';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../../components/DialogContentWithoutPadding';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import {
  removeWorkdayConfig,
  saveWorkdayWriteFields,
  useWorkdayConfig,
  WORKDAY_DEFAULT_WRITE_FIELDS
} from './service';

const NAME = 'Workday RaaS';

const WRITE_FIELD_TYPES: WorkdayWriteFieldType[] = [
  'text',
  'amount',
  'date',
  'boolean',
  'reference'
];

/**
 * Editor for the org's Workday Data Destination write-field catalog
 * (`WorkdayIntegrationConfig.writeFields`). Rows of label + key
 * ("<customObjectAlias>.<fieldAlias>") + value type. Saved by a direct
 * merge-write (no credential re-verification), independent of the RaaS
 * endpoints save above.
 */
const WriteFieldsSection = ({
  orgId,
  initial
}: {
  orgId: string;
  initial: WorkdayWriteField[];
}) => {
  const { t } = useUnsafeTranslation();
  const [rows, setRows] = useState<WorkdayWriteField[]>(initial);
  useEffect(() => setRows(initial), [initial]);

  const update = (i: number, patch: Partial<WorkdayWriteField>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const isValid = every(rows, (r) => !!r.label && r.key.includes('.'));
  const isChanged = !isEqual(rows, initial);

  return (
    <EndpointsContainer spacing={2} fullWidth>
      <Alert severity="info" icon={false}>
        {t(
          'Fields an onboarding task can write into Workday (Data Destinations). Key is the Web Service Alias path "<customObjectAlias>.<fieldAlias>".'
        )}
      </Alert>
      {rows.map((r, i) => (
        <EndpointContainer spacing={1} fullWidth key={i}>
          <CustomInputLabel label={t('Label')} fullWidth>
            <TextField
              variant="outlined"
              value={r.label}
              onChange={(ev) => update(i, { label: ev.target.value })}
              fullWidth
            />
          </CustomInputLabel>
          <CustomInputLabel
            label={t('Field key')}
            subLabel={t('"<customObjectAlias>.<fieldAlias>"')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={r.key}
              onChange={(ev) => update(i, { key: ev.target.value })}
              fullWidth
            />
          </CustomInputLabel>
          <CustomInputLabel label={t('Type')} fullWidth>
            <TextField
              select
              variant="outlined"
              value={r.type}
              onChange={(ev) =>
                update(i, { type: ev.target.value as WorkdayWriteFieldType })
              }
              fullWidth
            >
              {WRITE_FIELD_TYPES.map((ty) => (
                <MenuItem key={ty} value={ty}>
                  {ty}
                </MenuItem>
              ))}
            </TextField>
          </CustomInputLabel>
          <FlexContainer justifyContent="flex-end" fullWidth marginTop={1}>
            <Button
              size="small"
              color="primary"
              startIcon={<Trash2 size={14} />}
              onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
            >
              {t('Remove')}
            </Button>
          </FlexContainer>
        </EndpointContainer>
      ))}
      <FlexContainer justifyContent="space-between" fullWidth>
        <Button
          color="primary"
          onClick={() =>
            setRows((rs) => [...rs, { key: '', label: '', type: 'text' }])
          }
          startIcon={<PlusCircle size={16} />}
        >
          {t('Add field')}
        </Button>
        {rows.length === 0 && (
          <Button
            color="primary"
            onClick={() => setRows(WORKDAY_DEFAULT_WRITE_FIELDS)}
          >
            {t('Load default fields')}
          </Button>
        )}
      </FlexContainer>
      <FlexContainer justifyContent="flex-end" fullWidth marginTop={1}>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          onClick={() => saveWorkdayWriteFields(orgId, rows)}
          disabled={!isChanged || !isValid}
          pending={`${t('Saving')}...`}
        >
          {t('Save fields')}
        </ButtonWithPromise>
      </FlexContainer>
    </EndpointsContainer>
  );
};

const areEndpointsValidAndChanged = (
  next: WorkdayEndpointConfig[],
  prev: WorkdayEndpointConfig[]
) => {
  if (!next.length || isEqual(next, prev)) {
    return false;
  }
  return every(next, (e) => !!(e.name && e.url && e.username && e.password));
};

const EndpointsContainer = styled(FlexContainerVertical)((p) => ({
  padding: p.theme.spacing(4),
  paddingBottom: p.theme.spacing(2),
  paddingTop: p.theme.spacing(1)
}));

const EndpointContainer = styled(FlexContainerVertical)((p) => ({
  padding: p.theme.spacing(2),
  background: p.theme.custom.colors.gray.w100
}));

type State = {
  endpoints: WorkdayEndpointConfig[];
};

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<WorkdayIntegrationConfig>) => {
  const { t } = useUnsafeTranslation();

  const { endpoints } = config?.data || {
    endpoints: EMPTY_ARR
  };

  const [state, setState] = useState<State>({
    endpoints: endpoints.map((e) => ({
      ...e,
      password: masked(e.password)
    }))
  });
  useEffect(() => {
    setState({
      endpoints: endpoints.map((e) => ({
        ...e,
        password: masked(e.password)
      }))
    });
  }, [endpoints]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <DialogContentWithoutPadding>
        <EndpointsContainer spacing={2} fullWidth>
          <Alert severity="info" icon={false}>
            {t(
              'Connect Dado to Workday using "Reports as a Service" (Raas) to sync data about workers from Workday to Dado.'
            )}
          </Alert>
          {state.endpoints.map((e) => {
            const updateState = (
              fn: (prev: WorkdayEndpointConfig) => WorkdayEndpointConfig
            ) =>
              setState((s) => ({
                ...s,
                endpoints: s.endpoints.map((se) =>
                  se.id === e.id ? fn(se) : se
                )
              }));
            return (
              <EndpointContainer spacing={1} fullWidth key={e.id}>
                <CustomInputLabel
                  label={t('Endpoint Name')}
                  subLabel={t('For information only. Visible only in Dado')}
                  fullWidth
                >
                  <TextField
                    variant="outlined"
                    value={e.name}
                    onChange={(ev) =>
                      updateState((e) => ({
                        ...e,
                        name: ev.target.value
                      }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>

                <CustomInputLabel label={t('Import API URL')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.url}
                    onChange={(ev) =>
                      updateState((e) => ({
                        ...e,
                        url: ev.target.value
                      }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>

                <CustomInputLabel label={t('Username')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.username}
                    onChange={(ev) =>
                      updateState((e) => ({ ...e, username: ev.target.value }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>
                <CustomInputLabel label={t('Password')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.password}
                    onChange={(ev) =>
                      updateState((s) => ({ ...s, password: ev.target.value }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>
                <FlexContainer
                  justifyContent="flex-end"
                  fullWidth
                  marginTop={1}
                >
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<Trash2 size={14} />}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        endpoints: prev.endpoints.filter((se) => se.id !== e.id)
                      }))
                    }
                  >
                    {t('Remove')}
                  </Button>
                </FlexContainer>
              </EndpointContainer>
            );
          })}
          <Button
            color="primary"
            onClick={() =>
              setState((x) => ({
                ...x,
                endpoints: [
                  ...x.endpoints,
                  {
                    id: shortid(),
                    name: '',
                    url: '',
                    username: '',
                    password: ''
                  }
                ]
              }))
            }
            startIcon={<PlusCircle size={16} />}
          >
            {t('Add another endpoint')}
          </Button>
        </EndpointsContainer>
        <WriteFieldsSection
          orgId={config.data.orgId}
          initial={config.data.writeFields || []}
        />
      </DialogContentWithoutPadding>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeWorkdayConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!endpoints.length}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  'integrations_workday-verifyCredentialsAndStore',
                  {
                    orgId: config.data.orgId,
                    endpoints: state.endpoints
                  }
                ).then(onSuccessfulIntegration)
              }
              disabled={
                !areEndpointsValidAndChanged(
                  state.endpoints,
                  config.data.endpoints
                )
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const WorkdayRaasSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useWorkdayConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/workday.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
